/**
 * Outerwear silhouettes: hoodie, jacket, blazer, coat, parka, bomber,
 * denim_jacket, windcheater, gilet_vest.
 */
import { OUTLINE_ATTRS, DETAIL_ATTRS, DETAIL_FILLED_ATTRS, STITCH_ATTRS } from "./_attrs.js";
import { partGroup } from "./_part-attrs.js";
import { ribBand, pocketWelt, pocketPatch, zipFront, drawcords, yokeSeam, neckLabel } from "./_primitives.js";

import type { FlatView } from "../flat-svg.js";

/* ── Hoodie (hood, long sleeves, rib hem, drawcords) ───────────────── */
export function hoodieFlat(view: FlatView): string {
  return `<g>
  ${partGroup("body", `<path ${OUTLINE_ATTRS} d="M 154 96 L 100 112 L 56 244 L 38 350 L 96 364 L 118 276 L 132 192 L 130 418 L 270 418 L 268 192 L 282 276 L 304 364 L 362 350 L 344 244 L 300 112 L 246 96 Q 200 84 154 96 Z"/>
  ${ribBand(418, 130, 270, 14)}`)}
  ${partGroup("collar", view === "front"
    ? `<path ${DETAIL_FILLED_ATTRS} d="M 154 96 C 146 52 254 52 246 96 C 240 116 222 128 200 130 C 178 128 160 116 154 96 Z"/>
  <path ${STITCH_ATTRS} d="M 161 96 C 158 62 242 62 239 96"/>
  <path ${DETAIL_ATTRS} d="M 188 128 L 184 168 M 212 128 L 216 168"/>
  <rect x="181" y="168" width="6" height="14" rx="1.5" ${DETAIL_FILLED_ATTRS}/>
  <rect x="213" y="168" width="6" height="14" rx="1.5" ${DETAIL_FILLED_ATTRS}/>`
    : `<path ${DETAIL_FILLED_ATTRS} d="M 154 96 C 150 44 250 44 246 96 C 234 112 214 118 200 118 C 186 118 166 112 154 96 Z"/>
  <path ${STITCH_ATTRS} d="M 200 50 L 200 116"/>`)}
  ${view === "front" ? partGroup("pocket_left", `<path ${DETAIL_ATTRS} d="M 152 330 L 248 330 L 248 402 L 152 402 Z M 152 344 L 172 330 M 248 344 L 228 330"/>
  <path ${STITCH_ATTRS} d="M 157 336 L 167 330 M 243 336 L 233 330"/>`) : ""}
  ${partGroup("sleeve_left", `<path ${DETAIL_FILLED_ATTRS} d="M 38 350 L 96 364 L 90 386 L 32 372 Z"/>
  <path ${STITCH_ATTRS} d="M 100 114 Q 122 152 132 192"/>`)}
  ${partGroup("sleeve_right", `<path ${DETAIL_FILLED_ATTRS} d="M 362 350 L 304 364 L 310 386 L 368 372 Z"/>
  <path ${STITCH_ATTRS} d="M 300 114 Q 278 152 268 192"/>`)}
</g>`;
}

/* ── Jacket (zip front, pointed collar, hip-length) ───────────────── */
export function jacketFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 158 88 L 104 106 L 60 236 L 42 342 L 98 356 L 118 270 L 130 186 L 130 420 L 270 420 L 270 186 L 282 270 L 302 356 L 358 342 L 340 236 L 296 106 L 242 88 Q 200 76 158 88 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 42 342 L 98 356 L 92 378 L 36 364 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 358 342 L 302 356 L 308 378 L 364 364 Z"/>
  <path ${STITCH_ATTRS} d="M 104 108 Q 120 146 130 186"/>
  <path ${STITCH_ATTRS} d="M 296 108 Q 280 146 270 186"/>
  ${
    view === "front"
      ? `<path ${DETAIL_FILLED_ATTRS} d="M 162 86 C 174 76 226 76 238 86 L 248 100 L 200 122 L 152 100 Z"/>
  ${zipFront(122, 416)}
  ${pocketWelt(148, 340, 44)}
  ${pocketWelt(208, 340, 44)}`
      : `<path ${DETAIL_FILLED_ATTRS} d="M 162 86 C 174 76 226 76 238 86 L 244 100 L 156 100 Z"/>
  ${yokeSeam(136, 132, 268)}`
  }
  <path ${STITCH_ATTRS} d="M 132 412 L 268 412"/>
  <path ${STITCH_ATTRS} d="M 130 416 L 270 416"/>
</g>`;
}

/* ── Blazer (notch lapels, 2 buttons, structured) ─────────────────── */
export function blazerFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 158 86 L 106 104 L 64 232 L 46 340 L 102 354 L 120 268 L 132 184 L 132 424 L 268 424 L 268 184 L 280 268 L 298 354 L 354 340 L 336 232 L 294 104 L 242 86 Q 200 74 158 86 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 46 340 L 102 354 L 96 376 L 40 362 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 354 340 L 298 354 L 304 376 L 360 362 Z"/>
  <path ${STITCH_ATTRS} d="M 106 106 Q 122 144 132 184"/>
  <path ${STITCH_ATTRS} d="M 294 106 Q 278 144 268 184"/>
  ${
    view === "front"
      ? `<path ${DETAIL_FILLED_ATTRS} d="M 162 84 C 174 74 226 74 238 84 L 244 96 L 156 96 Z"/>
  <path ${DETAIL_ATTRS} d="M 176 96 L 160 140 L 180 300"/>
  <path ${DETAIL_ATTRS} d="M 224 96 L 240 140 L 220 300"/>
  <path ${DETAIL_ATTRS} d="M 156 96 L 168 130 L 146 134"/>
  <path ${DETAIL_ATTRS} d="M 244 96 L 232 130 L 254 134"/>
  <path ${STITCH_ATTRS} d="M 162 100 L 164 136"/>
  <path ${STITCH_ATTRS} d="M 238 100 L 236 136"/>
  <circle cx="192" cy="270" r="4.5" ${DETAIL_FILLED_ATTRS}/>
  <circle cx="192" cy="312" r="4.5" ${DETAIL_FILLED_ATTRS}/>
  ${pocketWelt(142, 340, 42)}
  ${pocketWelt(216, 340, 42)}
  ${pocketPatch(148, 170, 34, 38)}`
      : `<path ${DETAIL_FILLED_ATTRS} d="M 162 84 C 174 74 226 74 238 84 L 244 98 L 156 98 Z"/>
  <path ${DETAIL_ATTRS} d="M 200 86 L 200 424"/>
  <path ${STITCH_ATTRS} d="M 196 90 L 196 420"/>
  <path ${STITCH_ATTRS} d="M 204 90 L 204 420"/>`
  }
  <path ${STITCH_ATTRS} d="M 134 416 L 266 416"/>
</g>`;
}

/* ── Coat (long, below knee, wide lapels) ─────────────────────────── */
export function coatFlat(view: FlatView): string {
  const buttons = [220, 280, 340, 400]
    .map((y) => `<circle cx="194" cy="${y}" r="5" ${DETAIL_FILLED_ATTRS}/>`)
    .join("\n  ");
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 156 86 L 102 104 L 58 232 L 36 350 L 92 364 L 114 276 L 128 186 L 120 456 L 280 456 L 272 186 L 286 276 L 308 364 L 364 350 L 342 232 L 298 104 L 244 86 Q 200 72 156 86 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 36 350 L 92 364 L 86 386 L 30 372 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 364 350 L 308 364 L 314 386 L 370 372 Z"/>
  <path ${STITCH_ATTRS} d="M 102 106 Q 118 146 128 186"/>
  <path ${STITCH_ATTRS} d="M 298 106 Q 282 146 272 186"/>
  ${
    view === "front"
      ? `<path ${DETAIL_FILLED_ATTRS} d="M 160 84 C 172 74 228 74 240 84 L 246 96 L 154 96 Z"/>
  <path ${DETAIL_ATTRS} d="M 174 96 L 156 146 L 176 320"/>
  <path ${DETAIL_ATTRS} d="M 226 96 L 244 146 L 224 320"/>
  <path ${DETAIL_ATTRS} d="M 154 96 L 166 134 L 142 138"/>
  <path ${DETAIL_ATTRS} d="M 246 96 L 234 134 L 258 138"/>
  ${buttons}
  ${pocketWelt(136, 340, 44)}
  ${pocketWelt(220, 340, 44)}`
      : `<path ${DETAIL_FILLED_ATTRS} d="M 160 84 C 172 74 228 74 240 84 L 246 98 L 154 98 Z"/>
  <path ${DETAIL_ATTRS} d="M 200 86 L 200 450"/>
  <path ${STITCH_ATTRS} d="M 196 90 L 196 448"/>
  <path ${STITCH_ATTRS} d="M 204 90 L 204 448"/>`
  }
  <path ${STITCH_ATTRS} d="M 122 448 L 278 448"/>
</g>`;
}

/* ── Parka (long, hooded, drawcord waist, large pockets) ──────────── */
export function parkaFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 154 96 L 98 114 L 54 242 L 34 350 L 90 364 L 112 278 L 128 192 L 118 456 L 282 456 L 272 192 L 288 278 L 310 364 L 366 350 L 346 242 L 302 114 L 246 96 Q 200 84 154 96 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 34 350 L 90 364 L 84 386 L 28 372 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 366 350 L 310 364 L 316 386 L 372 372 Z"/>
  <path ${STITCH_ATTRS} d="M 98 116 Q 116 154 128 192"/>
  <path ${STITCH_ATTRS} d="M 302 116 Q 284 154 272 192"/>
  ${
    view === "front"
      ? `<path ${DETAIL_FILLED_ATTRS} d="M 154 96 C 146 48 254 48 246 96 C 240 118 222 130 200 132 C 178 130 160 118 154 96 Z"/>
  <path ${STITCH_ATTRS} d="M 161 96 C 158 58 242 58 239 96"/>
  ${zipFront(130, 450)}
  ${drawcords(200, 280)}
  <path ${DETAIL_ATTRS} d="M 126 280 L 274 280"/>
  ${pocketWelt(134, 340, 48)}
  ${pocketWelt(218, 340, 48)}`
      : `<path ${DETAIL_FILLED_ATTRS} d="M 154 96 C 150 42 250 42 246 96 C 234 114 214 120 200 120 C 186 120 166 114 154 96 Z"/>
  <path ${STITCH_ATTRS} d="M 200 48 L 200 118"/>`
  }
  <path ${STITCH_ATTRS} d="M 120 448 L 280 448"/>
</g>`;
}

/* ── Bomber (rib collar/cuffs/hem, zip front, cropped) ────────────── */
export function bomberFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 160 94 L 106 110 L 60 238 L 42 340 L 98 354 L 118 268 L 130 188 L 130 400 L 270 400 L 270 188 L 282 268 L 302 354 L 358 340 L 340 238 L 294 110 L 240 94 Q 200 86 160 94 Z"/>
  ${ribBand(88, 160, 240, 10)}
  ${ribBand(400, 130, 270, 14)}
  <path ${DETAIL_FILLED_ATTRS} d="M 42 340 L 98 354 L 92 376 L 36 362 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 358 340 L 302 354 L 308 376 L 364 362 Z"/>
  <path ${STITCH_ATTRS} d="M 106 112 Q 120 150 130 188"/>
  <path ${STITCH_ATTRS} d="M 294 112 Q 280 150 270 188"/>
  ${
    view === "front"
      ? `${zipFront(98, 396)}
  ${pocketWelt(140, 320, 42)}
  ${pocketWelt(218, 320, 42)}`
      : `${yokeSeam(138, 132, 268)}`
  }
</g>`;
}

/* ── Denim jacket (western yoke, button front, chest pockets) ─────── */
export function denim_jacketFlat(view: FlatView): string {
  const buttons = [160, 210, 260, 310, 360]
    .map((y) => `<circle cx="200" cy="${y}" r="4.5" ${DETAIL_FILLED_ATTRS}/>`)
    .join("\n  ");
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 158 88 L 106 106 L 62 234 L 44 338 L 100 352 L 118 268 L 130 186 L 130 416 L 270 416 L 270 186 L 282 268 L 300 352 L 356 338 L 338 234 L 294 106 L 242 88 Q 200 76 158 88 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 44 338 L 100 352 L 94 374 L 38 360 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 356 338 L 300 352 L 306 374 L 362 360 Z"/>
  <path ${STITCH_ATTRS} d="M 106 108 Q 120 146 130 186"/>
  <path ${STITCH_ATTRS} d="M 294 108 Q 280 146 270 186"/>
  ${
    view === "front"
      ? `<path ${DETAIL_FILLED_ATTRS} d="M 162 86 C 174 76 226 76 238 86 L 248 100 L 200 122 L 152 100 Z"/>
  <path ${DETAIL_ATTRS} d="M 194 122 L 192 412"/>
  <path ${DETAIL_ATTRS} d="M 206 122 L 208 412"/>
  <path ${STITCH_ATTRS} d="M 198 126 L 196 410"/>
  <path ${STITCH_ATTRS} d="M 202 126 L 204 410"/>
  ${buttons}
  ${pocketPatch(142, 176, 36, 40)}
  ${pocketPatch(222, 176, 36, 40)}
  <path ${DETAIL_ATTRS} d="M 142 176 L 178 192"/>
  <path ${DETAIL_ATTRS} d="M 258 176 L 222 192"/>`
      : `<path ${DETAIL_FILLED_ATTRS} d="M 162 86 C 174 76 226 76 238 86 L 244 100 L 156 100 Z"/>
  ${yokeSeam(128, 132, 268)}`
  }
  <path ${STITCH_ATTRS} d="M 132 408 L 268 408"/>
</g>`;
}

/* ── Windcheater (lightweight, zip front, packable hood) ──────────── */
export function windcheaterFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 156 92 L 104 108 L 60 236 L 42 340 L 98 354 L 116 268 L 130 186 L 130 420 L 270 420 L 270 186 L 284 268 L 302 354 L 358 340 L 340 236 L 296 108 L 244 92 Q 200 80 156 92 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 42 340 L 98 354 L 92 376 L 36 362 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 358 340 L 302 354 L 308 376 L 364 362 Z"/>
  <path ${STITCH_ATTRS} d="M 104 110 Q 120 148 130 186"/>
  <path ${STITCH_ATTRS} d="M 296 110 Q 280 148 270 186"/>
  ${
    view === "front"
      ? `<path ${DETAIL_FILLED_ATTRS} d="M 160 90 C 154 50 246 50 240 90 C 234 108 218 118 200 120 C 182 118 166 108 160 90 Z"/>
  <path ${STITCH_ATTRS} d="M 166 90 C 162 58 238 58 234 90"/>
  ${zipFront(118, 416)}
  ${pocketWelt(140, 310, 44)}
  ${pocketWelt(216, 310, 44)}`
      : `<path ${DETAIL_FILLED_ATTRS} d="M 160 90 C 156 46 244 46 240 90 C 230 106 214 112 200 112 C 186 112 170 106 160 90 Z"/>
  <path ${STITCH_ATTRS} d="M 200 50 L 200 110"/>`
  }
  <path ${STITCH_ATTRS} d="M 132 412 L 268 412"/>
</g>`;
}

/* ── Gilet / vest (sleeveless, zip front, quilted feel) ──────────── */
export function gilet_vestFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 160 86 Q 200 74 240 86 L 268 94 Q 284 108 278 136 Q 272 150 262 148 L 262 420 L 138 420 L 138 148 Q 128 150 122 136 Q 116 108 132 94 Z"/>
  <path ${STITCH_ATTRS} d="M 118 130 Q 130 146 142 142"/>
  <path ${STITCH_ATTRS} d="M 282 130 Q 270 146 258 142"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 160 86 Q 200 110 240 86"/>
  <path ${STITCH_ATTRS} d="M 163 91 Q 200 114 237 91"/>
  ${zipFront(108, 416)}
  ${pocketWelt(148, 310, 40)}
  ${pocketWelt(212, 310, 40)}
  <path ${DETAIL_ATTRS} d="M 140 190 L 260 190"/>
  <path ${DETAIL_ATTRS} d="M 140 230 L 260 230"/>
  <path ${DETAIL_ATTRS} d="M 140 270 L 260 270"/>`
      : `<path ${DETAIL_ATTRS} d="M 160 86 Q 200 102 240 86"/>
  <path ${STITCH_ATTRS} d="M 163 91 Q 200 106 237 91"/>
  ${neckLabel(200, 86)}
  <path ${DETAIL_ATTRS} d="M 140 190 L 260 190"/>
  <path ${DETAIL_ATTRS} d="M 140 230 L 260 230"/>
  <path ${DETAIL_ATTRS} d="M 140 270 L 260 270"/>`
  }
  <path ${STITCH_ATTRS} d="M 140 412 L 260 412"/>
</g>`;
}
